import React from 'react'
import {
  Flex,
  Label,
  Input,
  Button,
  Table,
  useDebounce,
  Loader,
} from '@fareye/ui'
import {
  FaDownload,
  isEmpty,
  dMmm,
  post,
  cancelToken,
} from '@fareye/utils'
import { downloadToCSV, useTranslation } from '@fareye/common'
import { getTableColumns } from './Column'
import { tripTabsBackground, modeBackground } from '@fareye/configs'

const style: any = {
  tripTabs: {
    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)',
    backgroundColor: tripTabsBackground,
    padding: '12px',
    height: 'fit-content',
  },
  downloadIcon: {
    marginLeft: '8px',
  },
  mode: {
    borderRadius: '4px',
    padding: '8px',
    backgroundColor: modeBackground,
    width: 'fit-content',
    marginBottom: '8px',
  },
}

type Alert = {
  alertName: string
  alertValue: string
  description: string
  timestamp: string
  videoLink: string
  imageLink: string
  latitude: string
  longitude: string
}

export const HEADER_INFLECTION = {
  headers: { 'Key-Inflection': 'camel', 'X-Change-Case': true },
}

export function LoadingSpinner({ title }: { title: string }) {
  const { t } = useTranslation()
  return (
    <div style={{ margin: '10px 0', textAlign: 'center' }}>
      <Loader
        containerStyle={{ height: '3em', marginBottom: '1em' }}
        variant="spin"
        color="rgba(24,144,255,1)"
      ></Loader>
      <Label m={3} variant="h6">
        {t(`${title}`)}
      </Label>
    </div>
  )
}

export const AiCamera = ({ config, data }: any) => {
  const { aiCameraDetails = [] } = data
  const { slug } = data
  const { t } = useTranslation()

  const [loading, setLoading] = React.useState(false)
  const source = cancelToken().source()
  const {
    config: { aiCameraColumns, links = {} },
  } = config
  const [searchInput, setSearchInput] = React.useState('')
  const debouncedSearchTerm = useDebounce(searchInput, 400)
  const [downloadLoading, setDownloadLoading] = React.useState(false)
  const [filteredData, setFilteredData] = React.useState<Alert[]>([])
  const [liveTrackingLink, setLiveTrackingLink] = React.useState('')
  const [playbackVideoLink, setPlaybackVideoLink] = React.useState('')

  const selectedColumn = React.useMemo(() => {
    return getTableColumns(aiCameraColumns, { t, links })
  }, [])

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const url = "/api/v3/journeys/external_alert_event"
        const json_params = { slug }
       
        const { data } = await post(url, json_params, source.token, HEADER_INFLECTION)
        const dataArray = data.content ?? []
        const liveLink = data.liveTrackingLink ?? ''
        const playbackLink = data.playbackVideoLink ?? ''

        setFilteredData(dataArray)
        setLiveTrackingLink(liveLink)
        setPlaybackVideoLink(playbackLink)

      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()

    return () => {
      source.cancel('Component unmounted.')
    }
  }, [slug])

  const getExportData = () => {
    const keysToExport = selectedColumn.map(col => col.id);
    return filteredData.map(row => {
      const filteredRow: Partial<Alert> = {};
      keysToExport.forEach((key) => {
        const k = key as keyof Alert;
        filteredRow[k] = row[k];
      });

      return filteredRow;
    });
  };

  const validateURL = (url: string) => {
    try {
      new URL(url); // Built-in URL constructor throws if invalid
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <Flex column>
      <Flex style={style.tripTabs} column>
        <Label style={{ fontSize: '16px', fontWeight: 600 }}>
          Alerts
        </Label>
        <Flex rowReverse>  
          <Button
            disabled={loading || isEmpty(filteredData) || downloadLoading}
            isLoading={downloadLoading}
            variant="s primary"
            style={{ height: '30px'}}
            onClick={() => {
              if (isEmpty(filteredData)) return;
              setDownloadLoading(true)
              const dataToDownload = getExportData();
              downloadToCSV({
                data: dataToDownload,
                fileName: `alertDetails_${dMmm()}`,
              })
              setDownloadLoading(false)
            }}
          >
            Download CSV
            <FaDownload style={style.downloadIcon} />
          </Button>
          <span title={!validateURL(liveTrackingLink)? 'Invalid or missing link': ''}>
          <a href={validateURL(liveTrackingLink) ? liveTrackingLink : undefined} target="_blank"
          style={{ textDecoration: 'none', cursor: !validateURL(liveTrackingLink) ? 'default' : 'pointer' }}>
            <Button
              variant="s primary"
              disabled={!validateURL(liveTrackingLink)}
              style={{ height: '30px', marginLeft: 'auto', marginRight: '10px' }}>
                Live Tracking
            </Button>
          </a>
          </span>
          <span title={!validateURL(playbackVideoLink)? 'Invalid or missing link': ''}>
          <a href={validateURL(playbackVideoLink) ? playbackVideoLink : undefined} target="_blank"
          style={{ textDecoration: 'none', cursor: !validateURL(playbackVideoLink) ? 'default' : 'pointer' }}>
            <Button
              variant="s primary"
              disabled={!validateURL(playbackVideoLink)}
              style={{ height: '30px', marginLeft: 'auto', marginRight: '10px' }}>
                Playback Video
            </Button>
          </a>
          </span>
        </Flex>
      </Flex>

      {loading ? (
        <LoadingSpinner
          title={"Loading ...."}
        />
      ) : !isEmpty(filteredData) ? (
        <Flex style={style.tripTabs} column>
          <Table
            className="striped responsive"
            totalElements={filteredData.length}
            sortable={false}
            showTotalCount={false}
            showPagination={false}
            data={filteredData}
            columns={selectedColumn}
          />
        </Flex>
      ) : (
        <Label
          style={{
            width: '100%',
            paddingTop: '10vmax',
            textAlign: 'center',
            display: 'block',
          }}
        >
          No Data
        </Label>
      )}
    </Flex>
  )
}
